import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon, ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import { track, ANALYTICS_EVENTS } from '../lib/analytics';
import LanguageSelector from './LanguageSelector';

const SECTION_IDS = ['solutions', 'process', 'pricing', 'contact'];
const CHALLENGES_URL = 'https://challenges.synapsis.team';

// Hidden for now — the subdomain stays live and reachable by direct link, it
// just is not advertised from the landing while the offer focuses on websites
// and systems. Flip to true to bring the nav link back; nothing else changes.
const SHOW_CHALLENGES_LINK = false;

const Navbar = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = SECTION_IDS
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    sections.forEach((section) => observerRef.current.observe(section));
    return () => observerRef.current?.disconnect();
  }, []);

  const navLinks = [
    { key: 'nav.solutions', id: 'solutions' },
    { key: 'nav.process', id: 'process' },
    { key: 'nav.pricing', id: 'pricing' },
    { key: 'nav.contact', id: 'contact' },
  ];

  const scrollTo = (id) => {
    setIsMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'bg-bg border-b border-white/8' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2 text-xl md:text-2xl font-bold text-ink"
          >
            <span className="w-2 h-2 rounded-full bg-accent-400" aria-hidden="true" />
            Synapsis
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <button
                key={link.key}
                onClick={() => scrollTo(link.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeSection === link.id
                    ? 'text-ink bg-white/8'
                    : 'text-gray-400 hover:text-ink hover:bg-white/5'
                }`}
              >
                {t(link.key)}
              </button>
            ))}
            {/* Real navigations, not scroll targets: these leave the landing page. */}
            <a
              href="/blog"
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-400 hover:text-ink hover:bg-white/5 transition-all duration-200"
            >
              {t('nav.blog')}
            </a>
            {SHOW_CHALLENGES_LINK && (
              <a
                href={CHALLENGES_URL}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-400 hover:text-ink hover:bg-white/5 transition-all duration-200 flex items-center gap-1.5"
              >
                {t('nav.challenges')}
                <ArrowUpRightIcon className="w-3.5 h-3.5" aria-hidden="true" />
              </a>
            )}
            <button
              onClick={() => { track(ANALYTICS_EVENTS.CTA_CLICKED, { location: 'navbar' }); scrollTo('contact'); }}
              className="ml-2 px-5 py-2 text-sm font-semibold text-white bg-accent-500 rounded-full hover:bg-accent-600 transition-colors duration-200"
            >
              {t('nav.cta')}
            </button>
          </div>

          {/* Right side: Language selector + mobile menu */}
          <div className="flex items-center space-x-2">
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-bg border-b border-white/8 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.key}
                  onClick={() => scrollTo(link.id)}
                  className={`block w-full text-left px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    activeSection === link.id ? 'text-ink bg-white/8' : 'text-gray-400 hover:text-ink hover:bg-white/5'
                  }`}
                >
                  {t(link.key)}
                </button>
              ))}
              <a
                href="/blog"
                className="block w-full px-4 py-3 text-base font-medium rounded-lg text-gray-400 hover:text-ink hover:bg-white/5 transition-colors"
              >
                {t('nav.blog')}
              </a>
              {SHOW_CHALLENGES_LINK && (
                <a
                  href={CHALLENGES_URL}
                  className="flex items-center gap-1.5 w-full px-4 py-3 text-base font-medium rounded-lg text-gray-400 hover:text-ink hover:bg-white/5 transition-colors"
                >
                  {t('nav.challenges')}
                  <ArrowUpRightIcon className="w-4 h-4" aria-hidden="true" />
                </a>
              )}
              <button
                onClick={() => { track(ANALYTICS_EVENTS.CTA_CLICKED, { location: 'navbar_mobile' }); scrollTo('contact'); }}
                className="block w-full mt-2 px-4 py-3 text-base font-semibold text-white bg-accent-500 rounded-lg text-center"
              >
                {t('nav.cta')}
              </button>
              <div className="pt-2 px-4">
                <LanguageSelector />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
